import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import {
  leggiImporto,
  leggiRimborso,
  salvaRimborso,
  type StatoRimborso,
  type TipoRimborso,
} from "../rimborsiApi";

/**
 * «Rimborso al cliente: quanto?» (Davide 03/11/2026). ManoMano, BricoBravo e Amazon non
 * dicono l'importo del rimborso. Compare solo se il server dice «da decidere» (canale giusto,
 * ordine in rimborso o cliente che ne parla, nessuna risposta ancora). Un clic salva.
 * Serve solo ai conti della BI: non tocca nessun documento fiscale.
 */
export function RimborsoManualeBanner({ riferimento }: { riferimento: string }) {
  const [stato, setStato] = useState<StatoRimborso | null>(null);
  const [importo, setImporto] = useState("");
  const [occupato, setOccupato] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  const [fatto, setFatto] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    setFatto(null);
    leggiRimborso(riferimento)
      .then((s) => vivo && setStato(s))
      // Il riquadro è un aiuto: se non si carica, la pagina ordine funziona lo stesso.
      .catch(() => vivo && setStato(null));
    return () => {
      vivo = false;
    };
  }, [riferimento]);

  async function salva(tipo: TipoRimborso) {
    const valore = leggiImporto(importo);
    if (tipo === "parziale" && valore === null) {
      setErrore("Scrivi quanto hai rimborsato, più di zero.");
      return;
    }
    setOccupato(true);
    setErrore(null);
    try {
      await salvaRimborso(riferimento, tipo, valore ?? undefined);
      setFatto(
        tipo === "totale"
          ? "Salvato: rimborso totale."
          : `Salvato: rimborso parziale di ${(valore ?? 0).toFixed(2).replace(".", ",")} €.`,
      );
      setStato(null);
    } catch (e) {
      setErrore(prettifyApiError(e));
    } finally {
      setOccupato(false);
    }
  }

  if (fatto) {
    return (
      <p
        role="status"
        className="mb-4 rounded-lg border border-green/30 bg-green-soft px-4 py-2.5 text-[13px] text-ink"
      >
        {fatto}
      </p>
    );
  }
  if (!stato || stato.stato !== "da_decidere") return null;

  return (
    <section
      aria-labelledby="rimborso-manuale-titolo"
      className="mb-4 rounded-xl border border-amber-500/50 bg-amber-50 px-4 py-3.5"
    >
      <h3 id="rimborso-manuale-titolo" className="text-[15px] font-bold leading-tight text-ink">
        Rimborso al cliente: quanto?
      </h3>
      <p className="mb-3 mt-1 text-[13px] text-ink">
        {stato.motivo === "messaggio"
          ? "Il cliente ha scritto di un rimborso."
          : "L'ordine è in rimborso."}{" "}
        Il canale non dice l'importo. Serve ai conti della BI.
      </p>
      <div className="flex flex-wrap items-end gap-2">
        <button
          type="button"
          disabled={occupato}
          onClick={() => void salva("totale")}
          className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-line-2 bg-card px-3 text-[13px] font-semibold text-ink transition-colors duration-150 hover:border-ink-4 hover:bg-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Totale
        </button>
        <label className="flex flex-col text-[12px] text-ink-3">
          Parziale: € (IVA compresa)
          <input
            type="text"
            inputMode="decimal"
            value={importo}
            onChange={(e) => setImporto(e.target.value)}
            placeholder="0,00"
            className="mt-1 min-h-[44px] w-32 rounded-md border border-line-2 bg-card px-3 text-[14px] text-ink focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50"
          />
        </label>
        <button
          type="button"
          disabled={occupato}
          onClick={() => void salva("parziale")}
          className="inline-flex min-h-[44px] items-center justify-center rounded-md border border-emerald-700 bg-emerald-700 px-4 text-[13px] font-semibold text-white transition-colors duration-150 hover:bg-emerald-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Salva parziale
        </button>
      </div>
      {errore && (
        <p role="alert" className="mt-2 text-[12px] text-red">
          {errore}
        </p>
      )}
    </section>
  );
}
